// React
import { useCallback, useReducer, useState } from "react";

// MUI
import { Skeleton } from "@mui/material";
import { GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid-premium";

// Helpers
import { extractFilteredRows, getIsButtonDisabled } from "@/DataPages/helpers/inventory/utils";
import { getLogger } from "@/Common/helpers/getLogger";

// Components
import VehicleInformationTable from "@DataPages/components/Inventory/Tables/VehicleInformationTable";
import VehicleDetailsDialog from "@DataPages/components/Inventory/Widgets/VehicleDetailsDialog";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import GPSInformationDialog from "@DataPages/components/Inventory/Widgets/GPSInformationDialog";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";

// Context
import { useInventoryContext } from "./contexts/InventoryFilterContext";
import { useInventoryPaginatedQuery } from "./hooks/useInventoryPaginatedQuery";
import { useFilteredData } from "./hooks/useFilteredData";

// Types
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";
import { Inventory } from "@DataPages/types/inventory/models/inventory";
import { VehicleCategory } from "@DataPages/types/inventory/commons";

// Constants
import { InventoryTab } from "./constants";

// Images
import emptyTable from "@/assets/images/commons/empty_table.png";
import { and } from "@/Common/helpers/sonar";

type VehicleInformationTabProps = {
	locationId: string;
	accountId: string;
	vehicleCategory: VehicleCategory;
	selectedPayPeriod: IPayPeriod | null;
	onShowMultipleGPSVehicleDetails: (selectedRows: GridRowSelectionModel) => void;
};

/************************************************* REDUCERS **********************************************/
const dialogsReducer = (
	state: { isVehicleDetailedInformationDialogOpen: boolean; isGPSInformationDialogOpen: boolean },
	action: { type: string; payload: "isVehicleDetailedInformationDialogOpen" | "isGPSInformationDialogOpen" }
) => {
	switch (action.type) {
		case "open":
			return { ...state, [action.payload]: true };
		case "close":
			return { ...state, [action.payload]: false };
		default:
			return state;
	}
};

export default function VehicleInformation(props: Readonly<VehicleInformationTabProps>) {
	const { selectedPayPeriod, onShowMultipleGPSVehicleDetails, locationId, accountId, vehicleCategory } = props;
	const log = getLogger("VehicleInformation");
	log("rendering VehicleInformation %O", props);
	// States
	const [selectedVehicle, setSelectedVehicle] = useState<Inventory | null>(null);
	const [dialogsState, dispatch] = useReducer(dialogsReducer, {
		isVehicleDetailedInformationDialogOpen: false,
		isGPSInformationDialogOpen: false,
	});
	const apiRef = useGridApiRef();
	const { commissionCycleId, payPeriodId } = useInventoryContext();

	const {
		data: allRows = [],
		isLoading,
		hasNextPage,
	} = useInventoryPaginatedQuery({
		locationId,
		vehicleCategory,
		commissionCycleId,
		payPeriodId,
		startDate: selectedPayPeriod?.startDate ?? new Date(),
		endDate: selectedPayPeriod?.endDate ?? new Date(),
		enabled: and(!!locationId, !!commissionCycleId, !!payPeriodId, !!selectedPayPeriod?.startDate, !!selectedPayPeriod?.endDate),
	});

	const inventoryTab = vehicleCategory === "new" ? InventoryTab.NEW_VEHICLES : InventoryTab.PREOWNED_VEHICLES;

	const rows = useFilteredData({
		data: allRows,
		currentOrigin: inventoryTab,
		pathToVinNumber: "vin",
		commissionCycleId,
		payPeriodId,
	});

	const [disabledButtons, setDisabledButtons] = useState({ prev: true, next: true });

	/************************************************* HANDLERS **********************************************/
	const handleToggleDetailedInformation = useCallback(
		(from: "isVehicleDetailedInformationDialogOpen" | "isGPSInformationDialogOpen", action: "open" | "close", vin?: string) => {
			log("handleToggleDetailedInformation %O", { from, action, vin });
			dispatch({ type: action, payload: from });
			if (vin) {
				if (!apiRef.current?.state) {
					return;
				}

				const filteredRows = extractFilteredRows(apiRef.current.state, from);
				const currentSelectedIndex = filteredRows.findIndex((vehicle) => vehicle.vin === vin);
				setDisabledButtons({
					prev: getIsButtonDisabled("prev", filteredRows, String(filteredRows[currentSelectedIndex].vin)),
					next: getIsButtonDisabled("next", filteredRows, String(filteredRows[currentSelectedIndex].vin)),
				});

				setSelectedVehicle(rows?.find((vehicle) => vehicle.vin === vin) ?? null);
			}
		},
		[apiRef, rows]
	);

	const handleNavigate = useCallback(
		(direction: "prev" | "next") => {
			log("handleNavigate %O", direction);
			if (!apiRef.current?.state) {
				return;
			}

			const from = dialogsState.isVehicleDetailedInformationDialogOpen
				? "isVehicleDetailedInformationDialogOpen"
				: "isGPSInformationDialogOpen";

			const filteredRows = extractFilteredRows(apiRef.current.state, from);

			const currentSelectedIndex = filteredRows.findIndex((vehicle) => vehicle.vin === selectedVehicle?.vin);
			if (currentSelectedIndex === -1) {
				handleToggleDetailedInformation(from, "close");
				setSelectedVehicle(null);
				return;
			}
			const nextIndex = direction === "next" ? currentSelectedIndex + 1 : currentSelectedIndex - 1;

			setDisabledButtons({
				prev: getIsButtonDisabled("prev", filteredRows, String(filteredRows[nextIndex].vin)),
				next: getIsButtonDisabled("next", filteredRows, String(filteredRows[nextIndex].vin)),
			});
			setSelectedVehicle(filteredRows[nextIndex] ?? null);
		},
		[apiRef, dialogsState, selectedVehicle, handleToggleDetailedInformation]
	);

	if (isLoading) {
		return (
			<>
				<TableLoader toolbar buttons={3} content rows={5} multiTitles={8} className={"!mt-7"} toolbarClasses={"!mb-7"} />
				<Skeleton variant={"rectangular"} width={200} className={"mt-7 rounded-md"} />
			</>
		);
	}

	if (!rows.length) {
		log("No Vehicles to show");
		return (
			<EmptyBody
				image={emptyTable}
				title={"No Vehicles to show"}
				bodyHeight={"490px"}
				sx={{
					boxShadow: "none !important",
					maxHeight: "530px",
				}}
			/>
		);
	}
	return (
		<>
			<VehicleInformationTable
				apiRef={apiRef}
				rows={rows}
				vehicleCategory={vehicleCategory}
				onShowDetailedInformation={handleToggleDetailedInformation}
				onShowMultipleGPSVehicleDetails={onShowMultipleGPSVehicleDetails}
				hasNextPage={hasNextPage}
				inventoryTab={inventoryTab}
			/>

			<VehicleDetailsDialog
				locationId={locationId}
				rows={rows.filter((row) => row.dmsId)}
				isOpen={dialogsState.isVehicleDetailedInformationDialogOpen}
				onClose={() => handleToggleDetailedInformation("isVehicleDetailedInformationDialogOpen", "close")}
				selectedVehicle={selectedVehicle as Inventory}
				onNavigate={handleNavigate}
				disabledButtons={disabledButtons}
			/>
			<GPSInformationDialog
				locationId={locationId}
				accountId={accountId}
				rows={rows.filter((row) => row.gpsId && row.gpsProtected)}
				isOpen={dialogsState.isGPSInformationDialogOpen}
				onClose={() => handleToggleDetailedInformation("isGPSInformationDialogOpen", "close")}
				assetLocation={"Some"}
				selectedVehicle={selectedVehicle as Inventory}
				onNavigate={handleNavigate}
				disabledButtons={disabledButtons}
			/>
		</>
	);
}
